import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Container, Grid, useMediaQuery, Theme } from '@mui/material';
import { CoreGlobalFooter } from '@care/navigation';
import { Divider } from '@care/react-component-lib';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useLazyQuery } from '@apollo/client';
// import { HTLBIDSlot } from '@care/htl-bid';
import getListPageData, {
  SegmentationListingPageProps,
} from '@/components/pages/segmentationListings/segmentationListingPageProps';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import {
  AmplitudeEvent,
  trackAmplitudeEvent,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
} from '@/lib/analyticsHelper';
import Breadcrumbs from '@/components/Breadcrumbs';
import FAQs from '@/components/FAQs';
import SeoStats from '@/components/SeoStats';
import Filters from '@/components/Filters/LegacyFilters/Filters';
import NavBar from '@/components/SeoGlobalNavbar';
import SearchResultsPagination from '@/components/SearchResults/Pagination';
import PageInfo from '@/components/PageInfo';
import SearchResultsController from '@/components/SearchResults/SearchResultsController';
import RightRails from '@/components/RightRails';
import SeoLinkList from '@/components/SeoLinkList';
import SeoConversionBox from '@/components/SeoConversionBox';
import WhenCareModal from '@/components/WhenCareModal';
import PageMeta from '@/components/PageMeta';
import JobHowItWorks from '@/components/JobHowItWorks';
import ReviewCard from '@/components/ReviewCard';
import TopReviewResults from '@/components/SearchResults/TopReviewResults';
import { event } from '@/utils/hotjarHelper';
import GET_SEARCH_RESULTS from '@/API/getSearchResults';
import { loadFilters, clearFilters } from '@/utils/filtersStorage';
import generateEnrollmentUrl from '@/utils/generateEnrollmentUrl';
import {
  ScreenName,
  ScreenTemplate,
  UI_FEATURE_FLAGS,
  SERVICE_ID_FOR_MEMBER,
  JOB_SEARCHING_FOR,
  TealiumSlot,
  LaunchDarklyEvaluationReasons,
  ServiceIdForMember,
  HTL_SLOTS,
} from '@/constants';
import Hero from '@/components/Hero';

import {
  getSearchResults as GetSearchResults,
  getSearchResultsVariables as GetSearchResultsVariables,
} from '@/__generated__/getSearchResults';
import {
  SEOSegmentationPageType,
  SEOSegmentationSearchingFor,
  SEOSegmentationFilterInput,
} from '@/__generated__/globalTypes';
import JobHero from '@/components/JobHero';
import ArticleCarousel from '@/components/ArticleCarousel';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import BackgroundCheckBanner from '@/components/BackgroundCheckBanner';
import { mapNavigationMenuContent } from '@/utils/mapNavigationMenuContent';
import { SegmentationFiltersContext } from '@/components/contexts/SegmentationFiltersContext';
import { FiltersModal } from '@/components/Filters/FiltersModal';
import HeaderText from '@/components/HeaderText';
import LocationFilter from '@/components/Filters/LocationFilter';
import { FiltersPopover } from '@/components/Filters/FiltersPopover';
import { SxClassProps } from '@/types/common';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import { StatsContentRibbon } from '@/components/ListPage/StatsContentRibbon';
import { Hero as AboveTheFoldHero } from '@/components/ListPage/Hero/Hero';
import { WhenDoYouWantAJob } from '@/components/ListPage/WhenDoYouWantAJob';
import { useLDClientFlags } from '@/components/hooks/useLDClientFlags';
import { GAMAds } from '@/components/GAMAds';

const classes: SxClassProps = {
  divider: (theme: Theme) => ({
    marginRight: -2,
    marginLeft: -2,
    marginTop: 3,

    [theme.breakpoints.up('sm')]: {
      marginRight: 0,
      marginLeft: 0,
    },
  }),
};

interface Query {
  seoSegmentId: string;
  initialPageNumber: number;
  pageData: Omit<
    SegmentationListingPageProps['pageData'],
    | 'seoSegmentationDetails'
    | 'seoSegmentationBreadcrumbs'
    | 'getSEOSegmentationIntro'
    | 'getSEOSegmentationStats'
    | 'getSEOSegmentationFAQs'
    | 'getSEOSegmentationLinkSections'
    | 'seoArticlesList'
    | 'seoNavigationLinkSection'
  > & {
    seoSegmentationDetails: any;
    seoSegmentationBreadcrumbs: any;
    getSEOSegmentationIntro: any;
    getSEOSegmentationFAQs: any;
    getSEOSegmentationLinkSections: any;
    seoArticlesList: any;
    seoNavigationLinkSection: any;
  };
  ipAddress: string;
  userGeoInfo: string;
  botInfo: string;
  userAgent: string;
  deviceChar?: any;
  testName: SegmentationListingPageProps['testName'];
  numberOfReviewsPerProfile?: number | null;
}
// eslint-disable-next-line complexity
const SegmentationListingPage = ({
  seoSegmentId,
  initialPageNumber,
  pageData,
  ipAddress,
  botInfo,
  userGeoInfo,
  userAgent,
  deviceChar,
  testName,
  numberOfReviewsPerProfile,
}: Query) => {
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const showGAMAds = GAMFlag?.variationIndex === 1;

  const {
    seoSegmentationSearchResults: {
      filters: initialFilters,
      pagination: initialPagination,
      caregiverSearchResults: initialCaregiverSearchResults,
      providerSearchResults: initialProviderSearchResults,
      jobSearchResults: initialJobSearchResults,
      archivedJobSearchResults: initialArchivedJobSearchResults,
    },
    seoSegmentationSearchResults: { tests: seoBackEndTests },
    getSEOSegmentationIntro: segmentationIntro,
    getSEOSegmentationFAQs: faqs,
    getSEOSegmentationLinkSections: segmentationLinkSections,
    seoArticlesList: seoArticles,
    seoNavigationLinkSection,
  } = pageData;

  const segmentationDetails = pageData.seoSegmentationDetails;
  const { seoPageMetadata, searchResultsHeader: initialSearchResultsHeader } = segmentationDetails;

  const breadcrumbs =
    pageData.seoSegmentationBreadcrumbs.__typename === 'SEOSegmentationBreadcrumbsSuccess'
      ? pageData.seoSegmentationBreadcrumbs.breadcrumbs
      : null;
  const caregiverSegmentationStats =
    pageData.seoSegmentationStats?.__typename === 'SEOSegmentationStatsSuccess'
      ? pageData.seoSegmentationStats.caregiverStats
      : null;

  const heroImage =
    pageData.seoSegmentationHeroImage.__typename === 'SEOSegmentationHeroImageSuccess'
      ? pageData.seoSegmentationHeroImage.heroImage
      : null;

  const [pageNumber, setPageNumber] = useState(initialPageNumber);
  const [filters, setFilters] = useState(initialFilters);

  const [pagination, setPagination] = useState(initialPagination);
  const [caregiverSearchResults, setCaregiverSearchResults] = useState(
    initialCaregiverSearchResults
  );
  const [providerSearchResults, setProviderSearchResults] = useState(initialProviderSearchResults);
  const [jobSearchResults, setJobSearchResults] = useState(initialJobSearchResults);
  const [archivedJobSearchResults, setArchivedJobSearchResults] = useState(
    initialArchivedJobSearchResults
  );

  const [searchResultsHeader, setSearchResultsHeader] = useState(initialSearchResultsHeader);

  const stringifyDeviceChar = JSON.stringify(deviceChar);

  const { searchingFor } = usePageAttributes();

  const hotJarEventsTestFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_1586_A_B_TEST_HOT_JAR_EVENTS_TEST.id
  );

  const daycareDirectoryFirstFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id
  );

  const aboveTheFoldTestFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3193_ABOVE_THE_FOLD_CONVERSION_TEST.id
  );

  const dayCareRobustCardFlag = useFeatureFlag(UI_FEATURE_FLAGS.SEO_3126_DAYCARE_ROBUST_CARDS.id);
  const isAboveTheFoldTest = aboveTheFoldTestFlag?.variationIndex === 2;
  const salcRobustProviderCardFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3169_SALC_ROBUST_PROVIDER_CARDS.id
  );

  const aboveTheFoldTestJobsFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS.id
  );

  const isAboveTheFoldTestJobs = aboveTheFoldTestJobsFlag?.variationIndex === 2;

  const showPetCareUserFilters =
    searchingFor === SEOSegmentationSearchingFor.PROVIDER &&
    segmentationDetails.serviceIdForMember === ServiceIdForMember.PET_CARE;

  const showSeniorCareUserFilters =
    searchingFor === SEOSegmentationSearchingFor.PROVIDER &&
    segmentationDetails.serviceIdForMember === ServiceIdForMember.SENIOR_CARE;

  const showChildCareUserFilters =
    (searchingFor === SEOSegmentationSearchingFor.PROVIDER ||
      searchingFor === SEOSegmentationSearchingFor.ALL_PROVIDER) &&
    (segmentationDetails.serviceIdForMember === ServiceIdForMember.CHILD_CARE ||
      segmentationDetails.serviceIdForMember === ServiceIdForMember.BABYSITTER ||
      segmentationDetails.serviceIdForMember === ServiceIdForMember.NANNY);

  const showHomeCareUserFilters =
    (searchingFor === SEOSegmentationSearchingFor.PROVIDER ||
      searchingFor === SEOSegmentationSearchingFor.ALL_PROVIDER) &&
    segmentationDetails.serviceIdForMember === ServiceIdForMember.HOUSEKEEPING;

  const isJobSegment = segmentationDetails.searchingFor in JOB_SEARCHING_FOR;

  const isNationalLocationFilterFlag =
    segmentationDetails.pageType === SEOSegmentationPageType.NATIONAL && !isJobSegment;

  const showBackgroundCheckChildCareBanner = searchingFor === SEOSegmentationSearchingFor.PROVIDER;

  const showCaregiversAboveReviews =
    searchingFor === SEOSegmentationSearchingFor.PROVIDER ||
    searchingFor === SEOSegmentationSearchingFor.ALL_PROVIDER ||
    searchingFor !== SEOSegmentationSearchingFor.SMB_PROVIDER ||
    dayCareRobustCardFlag?.variationIndex === 1 ||
    salcRobustProviderCardFlag?.variationIndex === 1;

  const showUserFilters =
    showPetCareUserFilters ||
    showSeniorCareUserFilters ||
    showChildCareUserFilters ||
    showHomeCareUserFilters;

  const showLocationFilter = segmentationDetails.pageType === SEOSegmentationPageType.NATIONAL;

  // Feature Flag view conditions

  let showCtaPlacementTest = false;
  if (
    segmentationDetails.serviceIdForMember?.includes(
      SERVICE_ID_FOR_MEMBER[segmentationDetails.serviceIdForMember]
    )
  ) {
    showCtaPlacementTest = true;
  }

  const searchFiltersRef = useRef<HTMLDivElement>(null);
  const locationFilterRef = useRef<HTMLDivElement>(null);

  const [getSearchResults, { loading: isLoading, called: isClientSearchResultsQuery }] =
    useLazyQuery<GetSearchResults, GetSearchResultsVariables>(GET_SEARCH_RESULTS, {
      variables: {
        filters: loadFilters(),
        pageNumber,
        seoSegmentId,
        ipAddress,
        botInfo,
        userGeoInfo,
        userAgent,
        hasFilters: isNationalLocationFilterFlag || showUserFilters,
        numberOfReviewsPerProfile,
      },
      onCompleted: (data: GetSearchResults) => {
        const searchResultData =
          data.seoSegmentationSearchResults.__typename === 'SEOSegmentationSearchResultsSuccess'
            ? data.seoSegmentationSearchResults
            : null;

        if (searchResultData) {
          setFilters(searchResultData.filters);
          setPagination(searchResultData.pagination);

          setCaregiverSearchResults(searchResultData.caregiverSearchResults);
          setProviderSearchResults(searchResultData.providerSearchResults);
          setJobSearchResults(searchResultData.jobSearchResults);
          setArchivedJobSearchResults(searchResultData.archivedJobSearchResults);
        }

        if (
          data.seoSegmentationDetails.__typename === 'SEOSegmentationDetailsSuccess' &&
          data?.seoSegmentationDetails.segmentationDetails !== null
        ) {
          setSearchResultsHeader(
            data?.seoSegmentationDetails.segmentationDetails.searchResultsHeader
          );
        }

        if (isNationalLocationFilterFlag) {
          locationFilterRef.current?.scrollIntoView();
        }
      },
    });
  const directoryFirstExperience =
    daycareDirectoryFirstFlag?.variationIndex === 1 &&
    daycareDirectoryFirstFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH;

  const getEnrollmentURL = (): string => {
    return generateEnrollmentUrl({
      router,
      serviceIdForMember: segmentationDetails.serviceIdForMember,
      serviceId: segmentationDetails.serviceId,
      isMobile: deviceChar.is_mobile === 'true',
      directoryFirstExperience,
    });
  };

  const enrollmentURL = getEnrollmentURL();

  const pageType =
    segmentationDetails.pageType === SEOSegmentationPageType.NATIONAL ? 'National' : 'Local';

  const seoPageType = () => {
    if (isJobSegment) {
      return 'Provider SEO page';
    }
    if (providerSearchResults && providerSearchResults.length > 0) {
      return 'SMB SEO page';
    }
    return 'Seeker SEO page';
  };

  const trackPageViewedEvent = async (
    pageTitle: string,
    serviceIdForMember: string,
    amplitudeTestName: string
  ) => {
    // FIXME: Will think more robust solution for this
    const amplitudeDataForPageViewed = {
      seo_page_title: pageTitle,
      seo_page_type: seoPageType(),
      service_id_for_member: serviceIdForMember,
      page_template: pageType,
    };

    const amplitudeDataForTestExposure = {
      test_name: amplitudeTestName,
      test_variant: '1',
      service_id_for_member: serviceIdForMember,
      page_template: pageType,
    };

    if (amplitudeTestName) {
      await trackAmplitudeEvent(AmplitudeEvent.testExposure, amplitudeDataForTestExposure);
    }
    await trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, amplitudeDataForPageViewed);
  };

  useEffect(() => {
    clearFilters();
  }, []);

  /*
   * Handling of page url from URL
   */
  useEffect(() => {
    trackPageViewedEvent(
      segmentationDetails.headline,
      segmentationDetails.serviceIdForMember,
      testName
    );

    if (seoBackEndTests) {
      seoBackEndTests.forEach((seoBETest) =>
        trackAmplitudeEvent(AmplitudeEvent.testExposure, {
          test_name: seoBETest.testName,
          test_variant: seoBETest.testVariant,
          service_id_for_member: segmentationDetails.serviceIdForMember,
          page_template: pageType,
        })
      );
    }
    if (hotJarEventsTestFlag && hotJarEventsTestFlag.reason.kind === 'RULE_MATCH') {
      if (typeof hotJarEventsTestFlag.variationIndex === 'number') {
        event(
          `${UI_FEATURE_FLAGS.SEO_1586_A_B_TEST_HOT_JAR_EVENTS_TEST.id}-variation-${hotJarEventsTestFlag.variationIndex}`
        );
      }
    }
    if (
      aboveTheFoldTestFlag &&
      aboveTheFoldTestFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3193_ABOVE_THE_FOLD_CONVERSION_TEST.id,
        test_variant: aboveTheFoldTestFlag.variationIndex,
        service_id_for_member: segmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      dayCareRobustCardFlag &&
      dayCareRobustCardFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3126_DAYCARE_ROBUST_CARDS.id,
        test_variant: dayCareRobustCardFlag.variationIndex,
        service_id_for_member: segmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      salcRobustProviderCardFlag &&
      salcRobustProviderCardFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3169_SALC_ROBUST_PROVIDER_CARDS.id,
        test_variant: salcRobustProviderCardFlag.variationIndex,
        service_id_for_member: segmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (
      aboveTheFoldTestJobsFlag &&
      aboveTheFoldTestJobsFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS.id,
        test_variant: aboveTheFoldTestJobsFlag.variationIndex,
        service_id_for_member: segmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    if (GAMFlag && GAMFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3487_GAM_ADS_SEO_MFE.id,
        test_variant: GAMFlag.variationIndex,
        service_id_for_member: segmentationDetails.serviceIdForMember,
        page_template: pageType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pageProperties = {
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
      seo_page_id: seoPageMetadata.seoPageId,
      service_id_for_member: seoPageMetadata.serviceIdForMember,
      slots: TealiumSlot.VISITOR,
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [
    stringifyDeviceChar,
    deviceChar,
    seoPageMetadata.seoPageId,
    seoPageMetadata.serviceIdForMember,
  ]);

  /*
   * Handling page clicks for pagination
   */
  const handlePageClick = (newPage: number) => {
    const filterSelected = loadFilters() || {};
    if (filters) {
      if (filters.location) {
        filterSelected.zipcode = filters.location.zipcode;
      }
    }

    searchFiltersRef.current?.scrollIntoView({ behavior: 'smooth' });

    setPageNumber(newPage);
    getSearchResults({
      variables: {
        filters: filterSelected,
        pageNumber: newPage,
        seoSegmentId,
        ipAddress,
        botInfo,
        userGeoInfo,
        userAgent,
        hasFilters: isNationalLocationFilterFlag || showUserFilters,
        numberOfReviewsPerProfile,
      },
    });
  };

  const handleFiltersApply = () => {
    handlePageClick(1); // pull Apply changes from localStorage but set page as 1
    router.push(seoSegmentId, undefined, { shallow: true });
  };

  // Component subrender methods
  const renderStats = () => {
    if (segmentationDetails?.pageType === SEOSegmentationPageType.NATIONAL) {
      if (
        caregiverSegmentationStats &&
        caregiverSearchResults.length > 0 &&
        (caregiverSegmentationStats?.totalProviders ?? 0) > 0
      ) {
        return <SeoStats {...caregiverSegmentationStats} />;
      }
    }
    return null;
  };

  const howItWorksDivider = (
    <Grid item xs={12}>
      {isMobile && (
        <Box sx={classes.divider}>
          <Divider />
        </Box>
      )}
    </Grid>
  );

  const showReviewCard =
    (caregiverSegmentationStats?.totalReviewCount ?? 0) >= 5 &&
    (caregiverSegmentationStats?.avgReviewRating ?? 0) >= 4;

  const navigationMenuContent = mapNavigationMenuContent(
    seoNavigationLinkSection?.seoNavigationLinkSection
  );

  const filtersCtx = useMemo(() => {
    return {
      filters,
      setFilters: (filterVals: any) => {
        setFilters(filterVals);
      },
      fetchResults: (filterVals: SEOSegmentationFilterInput, resetPage: boolean) => {
        searchFiltersRef.current?.scrollIntoView();
        getSearchResults({
          variables: {
            filters: filterVals,
            pageNumber: resetPage ? 1 : pageNumber,
            seoSegmentId,
            ipAddress,
            botInfo,
            userGeoInfo,
            userAgent,
            hasFilters: isNationalLocationFilterFlag || showUserFilters,
            numberOfReviewsPerProfile,
          },
        });

        if (resetPage) {
          setPageNumber(1);
          router.push(seoSegmentId, undefined, { shallow: true });
        }
      },
    };
  }, [
    filters,
    getSearchResults,
    pageNumber,
    seoSegmentId,
    ipAddress,
    botInfo,
    userGeoInfo,
    userAgent,
    numberOfReviewsPerProfile,
    isNationalLocationFilterFlag,
    showUserFilters,
    router,
  ]);

  const estimatedZipcode = () => {
    return getZipcodeFromGeoInfo(userGeoInfo);
  };

  const whenCareModalEnrollmentUrl = (() => {
    if (
      directoryFirstExperience &&
      segmentationDetails.serviceIdForMember === ServiceIdForMember.CHILD_CARE_LOCATION
    ) {
      return `${enrollmentURL}&zipcode=${estimatedZipcode()}`;
    }
    return `${enrollmentURL}`;
  })();

  return (
    <SegmentationFiltersContext.Provider value={filtersCtx}>
      <PageMeta
        title={seoPageMetadata.title}
        canonicalUrl={seoPageMetadata.canonicalURL}
        metaDescriptions={seoPageMetadata.metaDescriptions}
        metaKeywords={seoPageMetadata.metaKeywords}
        hrefLangLinks={seoPageMetadata.hrefLangLinks}
        paginationPageCount={initialPagination.numberOfPages}
        paginationCurrentPage={pageNumber}
      />

      {/*
        Global header/navbar for page
      */}
      <NavBar enrollmentURL={enrollmentURL} internalLinkingMenuContent={navigationMenuContent} />

      <Grid container>
        {/*
          HeroImage content with
          headline, sub-headline and heroImage
        */}

        {isJobSegment && !isAboveTheFoldTestJobs && (
          <Grid item xs={12}>
            <JobHero
              enrollmentURL={enrollmentURL}
              headline={segmentationDetails.headline}
              heroImage={heroImage}
              subHeadline={segmentationDetails.subHeadline}
            />
          </Grid>
        )}

        {isJobSegment && isAboveTheFoldTestJobs && (
          <>
            <AboveTheFoldHero
              enrollmentURL={enrollmentURL}
              headline={segmentationDetails.headline}
              subHeadline={segmentationDetails.subHeadline}
              userGeoInfo={userGeoInfo}
              directoryFirstExperience={directoryFirstExperience}
              isJobSegment={isJobSegment}
            />
            <WhenDoYouWantAJob enrollmentURL={enrollmentURL} />
          </>
        )}

        {!isJobSegment && !isAboveTheFoldTest && (
          <Grid
            item
            xs={12}
            sx={(theme: Theme) => ({ borderTop: `1px solid ${theme.palette.care.white}` })}>
            <Hero
              enrollmentURL={enrollmentURL}
              headline={segmentationDetails.headline}
              heroImage={heroImage}
              searchFiltersRef={searchFiltersRef}
              subHeadline={segmentationDetails.subHeadline}
              segmentationStats={caregiverSegmentationStats}
              userGeoInfo={userGeoInfo}
              directoryFirstExperience={directoryFirstExperience}
            />
          </Grid>
        )}

        {/* Above the fold test */}
        {!isJobSegment && isAboveTheFoldTest && (
          <>
            <AboveTheFoldHero
              enrollmentURL={enrollmentURL}
              headline={segmentationDetails.headline}
              subHeadline={segmentationDetails.subHeadline}
              userGeoInfo={userGeoInfo}
              directoryFirstExperience={directoryFirstExperience}
            />
            <StatsContentRibbon
              avgPayRate={caregiverSegmentationStats?.avgPayRate ?? null}
              avgReviewRating={caregiverSegmentationStats?.avgReviewRating ?? null}
              avgYearsOfExperience={caregiverSegmentationStats?.avgYearsOfExperience ?? null}
              totalProviders={caregiverSegmentationStats?.totalProviders ?? null}
            />
            <Box ref={searchFiltersRef} />
          </>
        )}

        {/*
          SeoConversion with CTAs and title
        */}
        {isJobSegment && !isAboveTheFoldTestJobs && (
          <Grid item xs={12}>
            <SeoConversionBox
              enrollmentURL={enrollmentURL}
              header={segmentationDetails.conversionHeader}
            />
          </Grid>
        )}
        {showBackgroundCheckChildCareBanner && !isAboveTheFoldTest && (
          <Grid item xs={12}>
            <BackgroundCheckBanner />
          </Grid>
        )}
        {/*
          Breadcrumb list for desktop version
        */}
        {((!isMobile && breadcrumbs.length > 1) || (breadcrumbs.length > 1 && !isJobSegment)) && (
          <Grid item xs={12}>
            <Box mt={isAboveTheFoldTest ? 0 : 3} mb={isAboveTheFoldTest ? 4 : -1}>
              <Container maxWidth="lg">
                <Breadcrumbs breadcrumbs={breadcrumbs} />
              </Container>
            </Box>
          </Grid>
        )}
        {isJobSegment && (
          <>
            {/*
          Filters with title and list (Jobs)
        */}

            <Grid item xs={12} mb={isAboveTheFoldTestJobs ? 7 : 0}>
              <Container maxWidth="lg">
                <Filters
                  searchFilters={segmentationDetails.searchFilters}
                  searchResultsHeader={searchResultsHeader}
                  onChange={handleFiltersApply}
                  searchFiltersRef={searchFiltersRef}
                  enrollmentURL={enrollmentURL}
                  serviceId={segmentationDetails.serviceId}
                  serviceIdForMember={segmentationDetails.serviceIdForMember}
                  showMoreFilters={caregiverSearchResults.length > 0}
                  showJobDetails={jobSearchResults.length > 0}
                  searchingFor={segmentationDetails.searchingFor}
                />
              </Container>
            </Grid>

            {/*
          Divider between filters and profiles
        */}
            {!isAboveTheFoldTestJobs && <Divider />}
          </>
        )}

        {/* {showGAMAds && (
          <Grid item xs={12} display="flex" justifyContent="center">
            <Box width="728px" height="90px">
              <HTLBIDSlot slotName={HTL_SLOTS.SEO_DIRECTORY_INLINE_1} />
            </Box>
          </Grid>
        )} */}

        <Grid item xs={12}>
          <Container maxWidth="lg">
            <Grid container>
              <Grid item xs={12} md={isJobSegment ? 7 : 12}>
                <Box pb={4}>
                  <Grid item xs={12}>
                    {!showCaregiversAboveReviews && (
                      <TopReviewResults
                        caregiverSearchResults={initialCaregiverSearchResults}
                        city={segmentationDetails.city}
                        pageType={segmentationDetails.pageType}
                        seoSegmentId={seoSegmentId}
                        state={segmentationDetails.state}
                        providerSearchResults={initialProviderSearchResults}
                      />
                    )}
                    <Box ref={locationFilterRef} />
                    {!isAboveTheFoldTest && !isAboveTheFoldTestJobs && <HeaderText />}
                    {/* Location Filter and User Filters */}
                    {!isAboveTheFoldTestJobs && (
                      <Box display="flex">
                        {showLocationFilter && <LocationFilter />}
                        {showUserFilters && !isMobile && <FiltersPopover />}
                      </Box>
                    )}
                    <SearchResultsController
                      isLoading={isLoading}
                      caregiverSearchResults={caregiverSearchResults}
                      providerSearchResults={providerSearchResults}
                      city={segmentationDetails.city}
                      conversionHeader={segmentationDetails.conversionHeader}
                      crossSell={segmentationDetails.crossSell}
                      enrollmentURL={enrollmentURL}
                      isClientSearchResultsQuery={isClientSearchResultsQuery}
                      jobSearchResults={jobSearchResults}
                      archivedJobSearchResults={archivedJobSearchResults}
                      pageType={segmentationDetails.pageType}
                      serviceId={segmentationDetails.serviceId}
                      serviceIdForMember={segmentationDetails.serviceIdForMember}
                      startIndex={pagination.startIndex}
                      state={segmentationDetails.state}
                      totalNumberOfResults={pagination.totalNumberOfResults}
                      segmentationStats={caregiverSegmentationStats}
                      smbHeadline={segmentationDetails.smbHeadline}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SearchResultsPagination
                      onPageClick={handlePageClick}
                      pagination={pagination}
                      segmentId={seoSegmentId}
                      pageSelected={pageNumber}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {showCaregiversAboveReviews && (
                      <TopReviewResults
                        caregiverSearchResults={initialCaregiverSearchResults}
                        city={segmentationDetails.city}
                        pageType={segmentationDetails.pageType}
                        seoSegmentId={seoSegmentId}
                        state={segmentationDetails.state}
                        providerSearchResults={initialProviderSearchResults}
                      />
                    )}
                  </Grid>
                  {/*
                    SeoLinkList with links
                  */}
                  <Grid item xs={12}>
                    <Grid container>
                      {!isJobSegment && segmentationLinkSections !== null && (
                        <SeoLinkList
                          seoLinkList={segmentationLinkSections}
                          internalLinkingHeadline={segmentationDetails.internalLinkingHeadline}
                        />
                      )}
                    </Grid>
                  </Grid>
                  {seoArticles?.seoArticlesList?.articles?.length > 0 && !isJobSegment && (
                    <Grid item xs={12}>
                      <ArticleCarousel articles={seoArticles.seoArticlesList.articles} />
                    </Grid>
                  )}
                  {/*
                  Breadcrumb list for mobile version (Jobs)
                */}
                  {isMobile && breadcrumbs.length > 1 && isJobSegment && (
                    <Grid item xs={12}>
                      <Breadcrumbs breadcrumbs={breadcrumbs} />
                    </Grid>
                  )}
                  {/*
                  Segmentation Intro
                */}
                  {segmentationIntro && (
                    <Grid item xs={12}>
                      <PageInfo segmentationIntro={segmentationIntro} />
                    </Grid>
                  )}
                  {/*
                  Divider for mobile version (Jobs)
                */}
                  {isMobile && isJobSegment && (
                    <Box sx={classes.divider}>
                      <Divider sx={classes.divider} />
                    </Box>
                  )}
                  {/*
                  Segmentation page statistics
                */}

                  {caregiverSegmentationStats && renderStats()}

                  {/*
                  SeoConversion with CTAs and title
                */}
                  {faqs && (
                    <>
                      <Grid item xs={12}>
                        <Box sx={showCtaPlacementTest ? undefined : classes.divider}>
                          {isJobSegment && (
                            <SeoConversionBox
                              header={segmentationDetails.conversionHeader}
                              enrollmentURL={enrollmentURL}
                            />
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <FAQs faqs={faqs} />
                      </Grid>

                      {isMobile && (
                        <Box sx={classes.divider}>
                          <Divider />
                        </Box>
                      )}
                    </>
                  )}
                  {showReviewCard && caregiverSegmentationStats && (
                    <Grid item xs={12}>
                      <ReviewCard stats={caregiverSegmentationStats} />
                    </Grid>
                  )}
                </Box>
              </Grid>
              {isJobSegment && (
                <Grid item xs={12} md={5}>
                  <Box
                    mt={1}
                    sx={{
                      width: '330px',
                      float: 'right',
                    }}>
                    <Grid container>
                      {/*
                      HowItWorks section content (Jobs)
                    */}
                      {segmentationDetails.pageType === 'NATIONAL' && (
                        <>
                          <JobHowItWorks enrollmentURL={enrollmentURL} />
                          {howItWorksDivider}
                        </>
                      )}
                      {/* GAM Ad on job directory */}
                      {showGAMAds && !isMobile && (
                        <GAMAds slotName={HTL_SLOTS.SEO_DIRECTORY_RR_1} />
                      )}
                      {/*
                      RightRails with links
                    */}
                      <RightRails rightRails={segmentationLinkSections} />
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Container>
        </Grid>
      </Grid>
      {/*
        When Do you Need Care Modal
      */}
      <WhenCareModal
        enrollmentURL={whenCareModalEnrollmentUrl}
        modalTitle={segmentationDetails.conversionHeader}
        pageType={pageType}
        serviceIdForMember={segmentationDetails.serviceIdForMember}
      />
      {/* Filters Modal */}
      {isMobile && showUserFilters && <FiltersModal />}
      {/*
        Global footer
      */}
      <CoreGlobalFooter />
    </SegmentationFiltersContext.Provider>
  );
};

export default SegmentationListingPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return getListPageData(context);
};
